import { styled } from '@linaria/react';
import { useTranslation } from 'react-i18next';
import type { SessionPullRequest } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { inkLight, secondaryBase } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { fmtNum } from '../../shared/formatters';
import { prStateColor, prStateIcon } from '../../shared/pr-state.helpers';
import { Additions, Deletions, StatePill } from './pr-detail.helpers';

type PrMetaRowProps = {
  pr: SessionPullRequest;
};

/**
 * One-line metadata for the selected pull request, shown in the Overview panel
 * under the PR title: state, repository, target branch, +/-, and the GitHub
 * link.
 *
 * @param {PrMetaRowProps} props - The selected pull request
 * @returns {JSX.Element} The metadata row
 */
export function PrMetaRow({ pr }: PrMetaRowProps) {
  const { t } = useTranslation();
  const stateColor = prStateColor(pr.state);

  return (
    <MetaRow>
      <StatePillColored $color={stateColor}>
        <Icon name={prStateIcon(pr.state)} size={13} color={stateColor} />
        {pr.state}
      </StatePillColored>
      {pr.repository_name && (
        <MetaItem>
          <Icon name="folder" size={14} color={inkLight} />
          {pr.repository_name}
        </MetaItem>
      )}
      {pr.target_branch && (
        <MetaItem>
          <Icon name="alt_route" size={14} color={inkLight} />
          {pr.target_branch}
        </MetaItem>
      )}
      <MetaItem>
        <Additions>+{fmtNum(pr.additions)}</Additions>
        <Deletions>−{fmtNum(pr.deletions)}</Deletions>
      </MetaItem>
      {pr.html_url && (
        <GithubLink href={pr.html_url} target="_blank" rel="noopener noreferrer">
          {t('agent_miner.pull_requests.open_on_github')}
          <Icon name="open_in_new" size={13} color={secondaryBase} />
        </GithubLink>
      )}
    </MetaRow>
  );
}

const StatePillColored = styled(StatePill)<{ $color: string }>`
  color: ${(props) => props.$color};
  border-color: ${(props) => props.$color};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  font: 500 12px/1 'Figtree', sans-serif;
  color: ${inkLight};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const GithubLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${secondaryBase};
  font: 600 12px/1 'Figtree', sans-serif;
  text-decoration: none;
`;
